import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import { useAuth } from "../lib/auth";
import { ShieldCheck, Trash2, Star, StarOff, UserCog, Users, LibraryBig, Save, UserX, Layers, UserPlus, Pencil, X, BarChart3, Download, Clock, BrainCircuit, UsersRound, Mail, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import BulkUsersImport from "../components/BulkUsersImport";
import AdminUserReport from "../components/AdminUserReport";
import InviteUserModal from "../components/InviteUserModal";
import BroadcastEmailModal from "../components/BroadcastEmailModal";
import ArchiveReasonModal from "../components/ArchiveReasonModal";
import { UserAccessModal, CourseAccessModal } from "../components/AccessModals";
import { api } from "../lib/api";

async function downloadCsv(path, fallbackName) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const disposition = res.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = (match && match[1]) || fallbackName;
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Download failed");
  }
}

async function downloadJson(path, fallbackName) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const disposition = res.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = (match && match[1]) || fallbackName;
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Download failed");
  }
}

function RestoreBackupButton({ onRestored }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const stamp = file.name || "the selected backup";
    const warn = `⚠️ This will WIPE the current database and replace it with the contents of:\n\n${stamp}\n\nYour own admin login will be preserved. Users, courses, cards, reviews, and activity will all be replaced.\n\nType RESTORE to confirm.`;
    const answer = window.prompt(warn, "");
    if (!answer || answer.trim().toUpperCase() !== "RESTORE") {
      toast.info("Restore cancelled");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const counts = Object.entries(data.restored || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      toast.success(`Restored ✓ (${counts})`);
      if (onRestored) onRestored();
      // reload to reflect fresh data
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFile}
        className="hidden"
        data-testid="admin-restore-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="admin-restore-button"
        className="brutal-btn-yellow text-xs py-2 disabled:opacity-50"
        title="Rehydrate the database from a downloaded backup JSON. Destructive."
      >
        <Save className="w-3.5 h-3.5" strokeWidth={3} /> {busy ? "Restoring…" : "Restore"}
      </button>
    </>
  );
}

function OverviewTiles({ classFilter }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const path = classFilter && classFilter !== "all" && classFilter !== "unassigned"
      ? `/admin/overview?class_year=${classFilter}`
      : "/admin/overview";
    api.get(path).then((r) => setData(r.data)).catch(() => setData(null));
  }, [classFilter]);
  if (!data) return null;
  const classLabel = data.class_year != null ? `Class ${data.class_year}` : "All classes";
  const tiles = [
    { label: "Active users today", value: data.active_users_today, sub: `of ${data.total_users} in ${classLabel}`, icon: UsersRound, color: "bg-brand-mint" },
    { label: "Minutes today", value: data.minutes_today, sub: `across ${classLabel.toLowerCase()}`, icon: Clock, color: "bg-brand-yellow" },
    { label: "Long-term words", value: data.words_long_term_total, sub: `interval ≥ ${data.long_term_threshold_days}d`, icon: BrainCircuit, color: "bg-brand-peach" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="admin-overview">
      {tiles.map((t) => (
        <div key={t.label} className={`brutal-card p-5 ${t.color}`} data-testid={`admin-overview-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="flex items-center gap-2 text-brand-navy/70 text-xs font-bold uppercase tracking-wide">
            <t.icon className="w-4 h-4" strokeWidth={3} /> {t.label}
          </div>
          <div className="font-display font-bold text-brand-navy text-4xl mt-1">{t.value}</div>
          <div className="text-xs text-brand-navy/60 mt-1">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

function ClassFilter({ value, onChange, testId }) {
  const [options, setOptions] = useState({ classes: [], unassigned: 0 });
  useEffect(() => {
    api.get("/admin/classes").then((r) => setOptions(r.data)).catch(() => {});
  }, []);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId || "admin-class-filter"}
      className="brutal-input py-2 text-sm font-bold"
    >
      <option value="all">All classes</option>
      {options.classes.map((c) => (
        <option key={c.class_year} value={String(c.class_year)}>
          Class {c.class_year} ({c.user_count})
        </option>
      ))}
      {options.unassigned > 0 && (
        <option value="unassigned">Unassigned ({options.unassigned})</option>
      )}
    </select>
  );
}

function Tab({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`brutal-badge px-4 py-2 ${active ? "bg-brand-yellow" : "bg-white hover:bg-brand-peach"}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={3} /> {children}
    </button>
  );
}

function UserFormModal({ open, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [classYear, setClassYear] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setEmail(initial?.email || "");
    setPassword("");
    setIsAdmin(!!initial?.is_admin);
    setClassYear(initial?.class_year != null ? String(initial.class_year) : "");
  }, [open, initial]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    if (!email.trim()) return toast.error("Email required");
    if (!isEdit && password.length < 6) return toast.error("Password must be 6+ chars");
    if (isEdit && password && password.length < 6) return toast.error("New password must be 6+ chars");
    const yearTrim = classYear.trim();
    let parsedYear = null;
    if (yearTrim) {
      const n = Number(yearTrim);
      if (!Number.isInteger(n) || n < 1900 || n > 2100) return toast.error("Class year must be a 4-digit year");
      parsedYear = n;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const payload = { name, email, is_admin: isAdmin };
        if (password) payload.password = password;
        // Preserve the "clear" intent: empty field → unset the class.
        if (parsedYear === null) payload.clear_class_year = true;
        else payload.class_year = parsedYear;
        const { data } = await api.patch(`/admin/users/${initial.id}`, payload);
        toast.success("User updated");
        onSaved?.(data);
      } else {
        const createPayload = { name, email, password, is_admin: isAdmin };
        if (parsedYear !== null) createPayload.class_year = parsedYear;
        const { data } = await api.post("/admin/users", createPayload);
        toast.success(`User "${data.name}" created`);
        onSaved?.(data);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brutal-card p-6 bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-user-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-bold text-brand-navy">
            {isEdit ? "Edit user" : "New user"}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-brand-cream rounded-full" title="Close">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-user-name" placeholder="Full name" className="brutal-input" autoComplete="name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-user-email" placeholder="Email" className="brutal-input" autoComplete="off" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-user-password"
            placeholder={isEdit ? "New password (leave blank to keep current)" : "Password (min 6 chars)"}
            className="brutal-input"
            autoComplete="new-password"
          />
          <div>
            <label className="text-xs font-bold uppercase text-brand-navy/70 tracking-wide">Class (year)</label>
            <input
              type="number"
              min="1900"
              max="2100"
              value={classYear}
              onChange={(e) => setClassYear(e.target.value)}
              data-testid="admin-user-class-year"
              placeholder="e.g. 2026 — leave blank for unassigned"
              className="brutal-input mt-1"
            />
          </div>
          <label className="flex items-center gap-3 py-2 cursor-pointer">
            <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} data-testid="admin-user-is-admin" className="w-5 h-5" />
            <span className="font-bold text-brand-navy">Admin access</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brutal-btn-white text-sm py-2">Cancel</button>
            <button type="submit" disabled={busy} data-testid="admin-user-save" className="brutal-btn-pink text-sm py-2">
              <Save className="w-4 h-4" strokeWidth={3} /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Create user")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsersTab({ classFilter = "all" }) {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [reportId, setReportId] = useState(null);
  const [inviteUser, setInviteUser] = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [bulkCourse, setBulkCourse] = useState("");
  const [archivedView, setArchivedView] = useState("active"); // active | archived
  // archivePrompt: { kind: 'single'|'bulk'|'class', user?, count?, classYear? }
  const [archivePrompt, setArchivePrompt] = useState(null);
  const { user: me } = useAuth();

  const load = async () => {
    const params = new URLSearchParams();
    if (classFilter === "unassigned") params.set("unassigned", "true");
    else if (classFilter && classFilter !== "all") params.set("class_year", classFilter);
    params.set("archived", archivedView);
    const { data } = await api.get(`/admin/users?${params.toString()}`);
    setUsers(data);
  };
  useEffect(() => { load(); setSelected(new Set()); }, [classFilter, archivedView]);
  useEffect(() => {
    // load courses once for the bulk-assign dropdown
    api.get("/courses").then((r) => setAvailableCourses(r.data || []));
  }, []);

  const toggleUser = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(users.map((u) => u.id)));
  const clearSelected = () => setSelected(new Set());

  const bulkAssign = async () => {
    if (!bulkCourse) { toast.error("Pick a course first"); return; }
    if (selected.size === 0) { toast.error("Select at least one user"); return; }
    try {
      const { data } = await api.post("/admin/access/bulk", {
        user_ids: Array.from(selected),
        course_ids: [bulkCourse],
        mode: "add",
      });
      toast.success(`Assigned to ${data.users_updated} user(s) · ${data.enrollments_created} new enrollment(s)`);
      clearSelected();
      setBulkCourse("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk assign failed");
    }
  };

  const toggle = async (u) => {
    if (u.id === me.id && u.is_admin) return toast.error("Cannot demote yourself");
    try {
      await api.patch(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
      toast.success(u.is_admin ? "Demoted" : "Promoted to admin");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const removeUser = async (u) => {
    if (u.id === me.id) return toast.error("Cannot delete yourself");
    const ok = window.confirm(
      `Delete "${u.name}" (${u.email})?\n\nThis will also delete all decks they created, plus their reviews, chat history, and voice recordings. This cannot be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(
        data.cascaded_courses
          ? `User deleted (also removed ${data.cascaded_courses} of their decks)`
          : "User deleted"
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const archiveUser = async (u, archive) => {
    if (u.id === me.id) return toast.error("Cannot archive yourself");
    if (archive) {
      setArchivePrompt({ kind: "single", user: u });
      return;
    }
    try {
      await api.post(`/admin/users/${u.id}/unarchive`);
      toast.success("User restored");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const bulkArchive = async () => {
    if (selected.size === 0) { toast.error("Select at least one user"); return; }
    const archive = archivedView === "active";
    if (archive) {
      setArchivePrompt({ kind: "bulk", count: selected.size });
      return;
    }
    // restore path — no reason needed
    if (!window.confirm(`Restore ${selected.size} selected user${selected.size === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-archive", {
        user_ids: Array.from(selected),
        archive: false,
      });
      toast.success(`${data.modified} user${data.modified === 1 ? "" : "s"} restored`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  const archiveWholeClass = () => {
    if (!classFilter || classFilter === "all" || classFilter === "unassigned") {
      toast.error("Pick a specific class year first (in the top filter)");
      return;
    }
    setArchivePrompt({ kind: "class", classYear: classFilter });
  };

  // Called by ArchiveReasonModal when admin confirms with a reason
  const submitArchive = async (reason) => {
    if (!archivePrompt) return;
    try {
      if (archivePrompt.kind === "single") {
        await api.post(`/admin/users/${archivePrompt.user.id}/archive`, { reason });
        toast.success("User archived");
      } else if (archivePrompt.kind === "bulk") {
        const { data } = await api.post("/admin/users/bulk-archive", {
          user_ids: Array.from(selected),
          archive: true,
          reason,
        });
        toast.success(`${data.modified} user${data.modified === 1 ? "" : "s"} archived`);
        setSelected(new Set());
      } else if (archivePrompt.kind === "class") {
        const { data } = await api.post("/admin/users/bulk-archive", {
          class_year: Number(archivePrompt.classYear),
          archive: true,
          reason,
        });
        toast.success(`Archived ${data.modified} user${data.modified === 1 ? "" : "s"} in Class ${archivePrompt.classYear}`);
        setSelected(new Set());
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed");
    }
  };

  return (
    <>
      <BulkUsersImport onImported={load} />
      <div className="mb-4 flex flex-wrap items-center gap-2 justify-end">
        <div className="flex items-center gap-1 mr-auto" data-testid="admin-archive-toggle">
          <button
            onClick={() => setArchivedView("active")}
            className={`text-xs font-bold px-3 py-2 border-2 border-brand-ink rounded-lg ${archivedView === "active" ? "bg-brand-pink text-white" : "bg-white text-brand-navy"}`}
            data-testid="admin-view-active"
          >
            Active
          </button>
          <button
            onClick={() => setArchivedView("archived")}
            className={`text-xs font-bold px-3 py-2 border-2 border-brand-ink rounded-lg ${archivedView === "archived" ? "bg-brand-navy text-white" : "bg-white text-brand-navy"}`}
            data-testid="admin-view-archived"
          >
            Archived
          </button>
        </div>
        {archivedView === "active" && classFilter && classFilter !== "all" && classFilter !== "unassigned" && (
          <button
            onClick={archiveWholeClass}
            data-testid="admin-archive-class-btn"
            className="brutal-btn-white text-sm py-2"
            title={`Archive every user in Class ${classFilter}`}
          >
            <UserX className="w-4 h-4" strokeWidth={3} /> Archive class {classFilter}
          </button>
        )}
        <button
          onClick={selectAllVisible}
          data-testid="admin-users-select-all-header"
          className="brutal-btn-white text-sm py-2"
          title="Select every user in the current filter for bulk actions"
        >
          Select all in filter
        </button>
        <button
          onClick={() => {
            const params = new URLSearchParams();
            if (classFilter === "unassigned") params.set("unassigned", "true");
            else if (classFilter && classFilter !== "all") params.set("class_year", classFilter);
            const q = params.toString() ? `?${params.toString()}` : "";
            const suffix = classFilter === "unassigned" ? "-unassigned" : classFilter !== "all" ? `-class-${classFilter}` : "";
            downloadCsv(`/admin/users.csv${q}`, `lingofacil-users${suffix}.csv`);
          }}
          data-testid="admin-users-csv-btn"
          className="brutal-btn-white text-sm py-2"
          title="Download users (respects the class filter)"
        >
          <Download className="w-4 h-4" strokeWidth={3} /> Download CSV
        </button>
        <button
          onClick={() => setBroadcastOpen(true)}
          data-testid="admin-broadcast-open"
          className="brutal-btn-mint text-sm py-2"
          title={selected.size > 0 ? `Email the ${selected.size} selected users` : "Email all users"}
        >
          <Mail className="w-4 h-4" strokeWidth={3} /> Email users
        </button>
        <button
          onClick={() => setModal({ open: true, initial: null })}
          data-testid="admin-user-new-btn"
          className="brutal-btn-pink text-sm py-2"
        >
          <UserPlus className="w-4 h-4" strokeWidth={3} /> New user
        </button>
      </div>

      {selected.size > 0 && (
        <div
          className="brutal-card p-3 mb-4 bg-brand-yellow flex flex-wrap items-center gap-3"
          data-testid="admin-bulk-bar"
        >
          <div className="font-bold text-brand-navy">
            {selected.size} selected
          </div>
          <select
            value={bulkCourse}
            onChange={(e) => setBulkCourse(e.target.value)}
            data-testid="admin-bulk-course-select"
            className="brutal-input py-1.5 text-sm font-bold flex-1 min-w-[200px]"
          >
            <option value="">— pick a course to assign —</option>
            {availableCourses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.flag ? `${c.flag} ` : ""}{c.title}
              </option>
            ))}
          </select>
          <button
            onClick={bulkAssign}
            data-testid="admin-bulk-assign-btn"
            className="brutal-btn-pink text-sm py-2"
          >
            <Lock className="w-4 h-4" strokeWidth={3} /> Assign course
          </button>
          <button
            onClick={selectAllVisible}
            data-testid="admin-bulk-select-all"
            className="brutal-btn-white text-sm py-2"
            title="Select every user matching the current class filter"
          >
            Select all visible
          </button>
          <button
            onClick={bulkArchive}
            data-testid="admin-bulk-archive-btn"
            className="brutal-btn-white text-sm py-2"
            title={archivedView === "active" ? "Archive selected users" : "Restore selected users"}
          >
            <UserX className="w-4 h-4" strokeWidth={3} /> {archivedView === "active" ? "Archive selected" : "Restore selected"}
          </button>
          <button
            onClick={clearSelected}
            data-testid="admin-bulk-clear"
            className="brutal-btn-white text-sm py-2"
          >
            Clear
          </button>
        </div>
      )}

      <div className="brutal-card bg-white overflow-hidden">
        <div className="divide-y-2 divide-brand-ink/10">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-4 p-4 flex-wrap">
              <input
                type="checkbox"
                checked={selected.has(u.id)}
                onChange={() => toggleUser(u.id)}
                data-testid={`admin-user-check-${u.id}`}
                className="w-5 h-5 shrink-0"
                title="Select for bulk actions"
              />
              <div className="w-10 h-10 rounded-full border-2 border-brand-ink bg-brand-blue text-white flex items-center justify-center font-display font-bold">
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-brand-navy truncate">{u.name}</div>
                <div className="text-sm text-brand-navy/60 truncate">{u.email}</div>
              </div>
              <div className="brutal-badge bg-brand-mint text-xs">{u.xp} XP</div>
              {u.class_year != null && (
                <div className="brutal-badge bg-brand-blue text-white text-xs" data-testid={`admin-user-class-${u.id}`}>
                  Class {u.class_year}
                </div>
              )}
              {u.is_admin && <div className="brutal-badge bg-brand-yellow text-xs"><ShieldCheck className="w-3 h-3" strokeWidth={3} /> Admin</div>}
              {u.archived && (
                <div
                  className="brutal-badge bg-brand-ink text-white text-xs"
                  data-testid={`admin-user-archived-${u.id}`}
                  title={u.archived_reason ? `Archived: ${u.archived_reason}` : "Archived"}
                >
                  Archived{u.archived_reason ? ` · ${u.archived_reason}` : ""}
                </div>
              )}
              <button
                onClick={() => setReportId(u.id)}
                data-testid={`admin-user-report-${u.id}`}
                className="brutal-btn-mint text-xs py-1.5"
                title="View engagement & memory report"
              >
                <BarChart3 className="w-3.5 h-3.5" strokeWidth={3} /> Report
              </button>
              <button
                onClick={() => setInviteUser(u)}
                data-testid={`admin-user-invite-${u.id}`}
                className="brutal-btn-yellow text-xs py-1.5"
                title="Send invitation email"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={3} /> Invite
              </button>
              <button
                onClick={() => setAccessUser(u)}
                data-testid={`admin-user-access-${u.id}`}
                className="brutal-btn-white text-xs py-1.5"
                title="Manage course access"
              >
                <Lock className="w-3.5 h-3.5" strokeWidth={3} /> Access
              </button>
              <button
                onClick={() => setModal({ open: true, initial: u })}
                data-testid={`admin-user-edit-${u.id}`}
                className="brutal-btn-yellow text-xs py-1.5"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={3} /> Edit
              </button>
              <button
                onClick={() => toggle(u)}
                data-testid={`admin-toggle-${u.id}`}
                className={`brutal-btn text-xs py-1.5 ${u.is_admin ? "bg-white" : "bg-brand-pink text-white"}`}
              >
                <UserCog className="w-3.5 h-3.5" strokeWidth={3} /> {u.is_admin ? "Demote" : "Make admin"}
              </button>
              <button
                onClick={() => archiveUser(u, !u.archived)}
                disabled={u.id === me.id}
                data-testid={`admin-archive-btn-${u.id}`}
                className={`brutal-btn text-xs py-1.5 ${u.archived ? "bg-brand-mint" : "bg-white"}`}
                title={u.archived ? "Restore this user" : "Archive this user (reversible)"}
              >
                <UserX className="w-3.5 h-3.5" strokeWidth={3} /> {u.archived ? "Restore" : "Archive"}
              </button>
              <button
                onClick={() => removeUser(u)}
                disabled={u.id === me.id}
                data-testid={`admin-delete-user-${u.id}`}
                className="brutal-btn-pink text-xs py-1.5"
                title={u.id === me.id ? "Can't delete yourself" : "Permanently delete (irreversible)"}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      </div>
      <UserFormModal
        open={modal.open}
        initial={modal.initial}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={load}
      />
      <AdminUserReport
        open={!!reportId}
        userId={reportId}
        onClose={() => setReportId(null)}
      />
      <InviteUserModal
        open={!!inviteUser}
        user={inviteUser}
        onClose={() => setInviteUser(null)}
      />
      <UserAccessModal
        open={!!accessUser}
        user={accessUser}
        onClose={() => setAccessUser(null)}
        onSaved={load}
      />
      <BroadcastEmailModal
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        selectedUserIds={Array.from(selected)}
      />
      <ArchiveReasonModal
        open={!!archivePrompt}
        onClose={() => setArchivePrompt(null)}
        onConfirm={submitArchive}
        title={
          archivePrompt?.kind === "single"
            ? `Archive ${archivePrompt.user?.name || "user"}`
            : archivePrompt?.kind === "class"
              ? `Archive class ${archivePrompt.classYear}`
              : `Archive ${archivePrompt?.count || 0} selected user${archivePrompt?.count === 1 ? "" : "s"}`
        }
        subtitle={
          archivePrompt?.kind === "single"
            ? `${archivePrompt.user?.email} will lose access. All their data stays and this is reversible.`
            : archivePrompt?.kind === "class"
              ? `Every student in Class ${archivePrompt.classYear} will lose access. Data is kept and this is reversible.`
              : "Selected users will lose access. Data is kept and this is reversible."
        }
      />
    </>
  );
}

function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [accessCourse, setAccessCourse] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/courses");
    setCourses(data);
  };
  useEffect(() => { load(); }, []);

  const toggleOfficial = async (c) => {
    try {
      await api.patch(`/admin/courses/${c.id}`, { is_official: !c.is_official });
      toast.success(c.is_official ? "Marked community" : "Marked official");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its cards? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/courses/${c.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/courses/${editing.id}`, {
        title: editing.title,
        description: editing.description,
        level: editing.level,
        color: editing.color,
      });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <>
      <div className="brutal-card bg-white overflow-hidden">
        <div className="divide-y-2 divide-brand-ink/10">
          {courses.map((c) => (
            <div key={c.id} data-testid={`admin-course-${c.id}`} className="flex items-center gap-4 p-4 flex-wrap">
              <div className="w-12 h-12 rounded-xl border-2 border-brand-ink flex items-center justify-center text-2xl shrink-0" style={{ backgroundColor: c.color || "#FFD166" }}>
                {c.flag}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-brand-navy truncate">{c.title}</div>
                <div className="text-sm text-brand-navy/60 truncate">
                  {c.language} · {c.card_count} cards · {c.creator?.name || "Official"}
                </div>
              </div>
              {c.is_official && <div className="brutal-badge bg-brand-yellow text-xs"><Star className="w-3 h-3" strokeWidth={3} /> Official</div>}
              <button onClick={() => toggleOfficial(c)} data-testid={`admin-toggle-official-${c.id}`} className="brutal-btn-white text-xs py-1.5">
                {c.is_official ? <StarOff className="w-3.5 h-3.5" strokeWidth={3} /> : <Star className="w-3.5 h-3.5" strokeWidth={3} />}
                {c.is_official ? "Unfeature" : "Feature"}
              </button>
              <Link to={`/admin/courses/${c.id}/cards`} data-testid={`admin-cards-${c.id}`} className="brutal-btn-mint text-xs py-1.5">
                <Layers className="w-3.5 h-3.5" strokeWidth={3} /> Cards ({c.card_count})
              </Link>
              <button onClick={() => setAccessCourse(c)} data-testid={`admin-course-access-${c.id}`} className="brutal-btn-white text-xs py-1.5" title="Manage access">
                <Lock className="w-3.5 h-3.5" strokeWidth={3} /> Access
              </button>
              <button onClick={() => setEditing({ ...c })} data-testid={`admin-edit-${c.id}`} className="brutal-btn-yellow text-xs py-1.5">
                Edit
              </button>
              <button onClick={() => remove(c)} data-testid={`admin-delete-${c.id}`} className="brutal-btn-pink text-xs py-1.5">
                <Trash2 className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-brand-ink/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="brutal-card p-6 bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold text-brand-navy mb-4">Edit course</h3>
            <div className="space-y-3">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="admin-edit-title" placeholder="Title" className="brutal-input" />
              <input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-edit-description" placeholder="Description" className="brutal-input" />
              <input value={editing.level || ""} onChange={(e) => setEditing({ ...editing, level: e.target.value })} data-testid="admin-edit-level" placeholder="Level" className="brutal-input" />
              <input value={editing.color || ""} onChange={(e) => setEditing({ ...editing, color: e.target.value })} data-testid="admin-edit-color" placeholder="#FFD166" className="brutal-input" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} data-testid="admin-edit-cancel" className="brutal-btn-white text-sm py-2">Cancel</button>
              <button onClick={saveEdit} data-testid="admin-edit-save" className="brutal-btn-pink text-sm py-2"><Save className="w-4 h-4" strokeWidth={3} /> Save</button>
            </div>
          </div>
        </div>
      )}

      <CourseAccessModal
        open={!!accessCourse}
        course={accessCourse}
        onClose={() => setAccessCourse(null)}
        onSaved={load}
      />
    </>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("courses");
  const [classFilter, setClassFilter] = useState("all");

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen grain-bg" data-testid="admin-page">
      <Nav />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="brutal-badge bg-brand-pink text-white mb-3"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} /> Admin</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">Studio</h1>
            <p className="text-brand-navy/70 mt-2 text-lg">Manage courses and users across LingoFacil.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                toast.info("Preparing backup…");
                await downloadJson("/admin/backup", "lingofacil-backup.json");
                toast.success("Backup downloaded — drop it into your OneDrive folder");
              }}
              data-testid="admin-backup-download"
              className="brutal-btn-mint text-xs py-2"
              title="Download a full JSON snapshot of the database (users, courses, cards, reviews, activity)."
            >
              <Download className="w-3.5 h-3.5" strokeWidth={3} /> Backup DB
            </button>
            <RestoreBackupButton />
            <span className="text-xs font-bold uppercase text-brand-navy/60 tracking-wide">Filter by class</span>
            <ClassFilter value={classFilter} onChange={setClassFilter} testId="admin-page-class-filter" />
          </div>
        </div>

        <OverviewTiles classFilter={classFilter} />

        <div className="flex gap-2 mb-6">
          <Tab active={tab === "courses"} onClick={() => setTab("courses")} icon={LibraryBig} testId="admin-tab-courses">Courses</Tab>
          <Tab active={tab === "users"} onClick={() => setTab("users")} icon={Users} testId="admin-tab-users">Users</Tab>
        </div>

        {tab === "courses" ? <CoursesTab /> : <UsersTab classFilter={classFilter} />}
      </div>
    </div>
  );
}
